import moment from 'moment';
import { Button } from 'antd';
import type { Patient } from '@/models/patient';
import styles from './PatientCompareCard.module.css';

interface PatientCompareCardProps {
  patient: Patient;
  isPrimary: boolean;
  onSetPrimary: () => void;
  studyCount: number;
}

const DASH = '—';

const valueOr = (v: string | null | undefined) => {
  const t = (v ?? '').trim();
  return t || DASH;
};

const sexLabel = (sex: Patient['sex']) => {
  switch (sex) {
    case 'MALE':
      return 'Male';
    case 'FEMALE':
      return 'Female';
    case 'OTHER':
      return 'Other';
    default:
      return DASH;
  }
};

const formatDOB = (dob: string) =>
  dob ? moment(dob).format('MMM D, YYYY') : DASH;

const formatReferring = (p: Patient) => {
  const full = `${(p.referringPhysicianFirstName ?? '').trim()} ${(
    p.referringPhysicianLastName ?? ''
  ).trim()}`.trim();
  return full || DASH;
};

const formatName = (p: Patient) =>
  `${(p.firstName ?? '').trim()} ${(p.lastName ?? '').trim()}`.trim() || DASH;

export default function PatientCompareCard({
  patient,
  isPrimary,
  onSetPrimary,
  studyCount,
}: PatientCompareCardProps) {
  const rows: [string, string][] = [
    ['Name', formatName(patient)],
    ['MRN', valueOr(patient.medicalRecordNumber)],
    ['DOB', formatDOB(patient.dateOfBirth)],
    ['Sex', sexLabel(patient.sex)],
    ['Referring Physician', formatReferring(patient)],
    ['Address', valueOr(patient.address)],
    ['Phone Number', valueOr(patient.phoneNumber)],
    ['Ethnicity', valueOr(patient.ethnicity)],
    ['Notes', valueOr(patient.notes)],
    ['Study Count', String(studyCount)],
  ];

  return (
    <div
      className={`${styles.card} ${isPrimary ? styles.primary : ''}`}
      data-primary={isPrimary || undefined}
    >
      <div className={styles.header}>
        <div className={styles.headerName}>{formatName(patient)}</div>
        <Button
          type={isPrimary ? 'primary' : 'default'}
          size="small"
          onClick={onSetPrimary}
          className={styles.primaryBtn}
        >
          {isPrimary ? 'Keep as Primary' : 'Set as Primary'}
        </Button>
      </div>
      <dl className={styles.rows}>
        {rows.map(([label, value]) => (
          <div className={styles.row} key={label}>
            <dt className={styles.label}>{label}</dt>
            <dd className={styles.value}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
